import styles from "./templateView.module.scss";
import { Checkbox } from "../../../shared/components/checkbox/checkbox";
import { Accordion } from "../../../shared/components/accordion/accordion";
import arrowIcon from "../../../shared/images/arrow.svg";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useGetAllEnvironmentsQuery } from "../../../api/environmentService";
import { useGetAllSectionsQuery } from "../../../api/testrailService";
import {
  useEffect,
  useMemo, useState
} from "react";
import { Section } from "../../../types/testrailTypes";
import { MethodType } from "../../../shared/components/methodType/methodType";
import { StatusCode } from "../../../types/sharedTypes";
import { useGetAllFolderQuery } from "../../../api/testCaseTemplateFolderService";
import { Folder } from "../../../types/testCaseTemplateFolderTypes";
import {getServer, transformTemplateForUser} from "../../../utils/testCaseUtils";
import { RadioGroup } from "../../../shared/components/radioGroup/radioGroup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  object,
  string
} from "yup";
import { useGetAllPrioritiesQuery } from "../../../api/priorityService";
import { Priority } from "../../../types/priorityTypes";
import {TestCaseTemplate} from "../../../types/testCaseTemplateTypes";

export const TemplateView = () => {
  const { t } = useTranslation();

  const statusCodes = useSelector((state: RootState) => state.mainSlice.statusCodes);
  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const selectedTestCaseTemplate = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCaseTemplate);

  const [localSelectedTestCaseTemplate, setLocalSelectedTestCaseTemplate] = useState<TestCaseTemplate>(transformTemplateForUser(selectedVariables, selectedTestCaseTemplate) || selectedTestCaseTemplate);

  const { data: environments } = useGetAllEnvironmentsQuery("");
  const { data: sections} = useGetAllSectionsQuery("", { skip: !localSelectedTestCaseTemplate.sectionId, refetchOnMountOrArgChange: true });
  const { data: folders } = useGetAllFolderQuery("");
  const { data: priorities } = useGetAllPrioritiesQuery("");

  const schema = object({
    isPositive: string().required(),
  });

  const { control, setValue } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    setLocalSelectedTestCaseTemplate(transformTemplateForUser(selectedVariables, selectedTestCaseTemplate) || selectedTestCaseTemplate);
  }, [setLocalSelectedTestCaseTemplate, selectedVariables, selectedTestCaseTemplate]);

  useEffect(() => {
    if (!!setValue) {
      setValue("isPositive", localSelectedTestCaseTemplate.isPositive ? "1" : "0")
    }
  }, [localSelectedTestCaseTemplate, setValue]);

  const section: Section | undefined = useMemo(() => {
    return sections?.find(section => section.id === localSelectedTestCaseTemplate.sectionId);
  }, [sections, localSelectedTestCaseTemplate]);

  const statusCode: StatusCode | undefined = useMemo(() => {
    return statusCodes.find(statusCode => statusCode.id === localSelectedTestCaseTemplate.statusCodeId);
  }, [statusCodes, localSelectedTestCaseTemplate]);

  const folder: Folder | undefined = useMemo(() => {
    return folders?.find(folder => folder.id === localSelectedTestCaseTemplate.testCaseTemplateFolderId);
  }, [folders, localSelectedTestCaseTemplate]);

  const priority: Priority | undefined = useMemo(() => {
    return priorities?.find(priority => priority.id === localSelectedTestCaseTemplate.priorityId);
  }, [priorities, localSelectedTestCaseTemplate]);

  return (
    <div className={styles.container}>
      <div className={styles.mainInfo}>
        <div className={styles.inputs}>
          <div className={styles.inputContainer}>
            <span className={styles.required}>{t("testCaseTemplate.name")}</span>
            <div className={styles.input}>
              <span title={localSelectedTestCaseTemplate.name}>
                {localSelectedTestCaseTemplate.name}
              </span>
            </div>
          </div>
          <div className={styles.radioGroup}>
            <RadioGroup disabled={true}
                        control={control}
                        name="isPositive"
                        options={[
                          {title: t("testCaseTemplate.positive"), value: "1"},
                          {title: t("testCaseTemplate.negative"), value: "0"}
                        ]}/>
          </div>
          <div className={`${styles.textAreaContainer} ${styles.description}`}>
            <div className={styles.title}>
              {t("common.description")}
            </div>
            <div className={styles.textarea}>
              {localSelectedTestCaseTemplate.description}
            </div>
          </div>
          <div className={styles.checkbox}>
            <Checkbox isChecked={!!localSelectedTestCaseTemplate?.sectionId} disabled={true}/>
            <span>{t("testCase.exportToTestRail")}</span>
          </div>
          <div className={styles.inputContainer}>
            <span>{t("testCase.section")}</span>
            <div className={styles.dropdown}>
              <span title={section?.name}>{section?.name}</span>
            </div>
          </div>
        </div>

        <div className={styles.inputs}>
          <div className={styles.inputContainer}>
            <span>{t("testCase.method")}</span>
            <div className={styles.dropdown}>
              <MethodType methodId={localSelectedTestCaseTemplate.methodId}/>
            </div>
          </div>
          <div className={styles.inputContainer}>
            <span>{t("testCase.expectedStatus")}</span>
            <div className={styles.dropdown}>
              <span title={`${statusCode?.code} ${statusCode?.name}`}>{`${statusCode?.code} ${statusCode?.name}`}</span>
            </div>
          </div>
          <div className={styles.inputContainer}>
            <span className={styles.required}>{t("common.maxExecutionTimeout")}</span>
            <div className={styles.input}>
              <span>{localSelectedTestCaseTemplate.maximumExecutionTimeout}</span>
            </div>
          </div>
          <div className={styles.inputContainer}>
            <span className={styles.required}>{t("testCaseTemplate.folder")}</span>
            <div className={styles.dropdown}>
              <span title={folder?.name}>{folder?.name}</span>
            </div>
          </div>
          <div className={styles.inputContainer}>
            <span className={styles.required}>{t("testCaseTemplate.priority")}</span>
            <div className={styles.dropdown}>
              <span title={priority?.name}>{priority?.name}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.environments}>
        <Accordion activeIndex={0}
                   activeClassName={styles.active}
                   maxHeight={400}
                   disabledAutoScroll
                   data={[{
                     title: (
                       <div className={styles.accordionTitle}>
                         <img src={arrowIcon} alt=""
                              className={styles.arrow}/>
                         <span>{t("testCaseTemplate.environments")}</span>
                       </div>
                     ),
                     child: (
                       <div className={styles.accordionChild}>
                         <div className={styles.inputs}>
                           <div className={styles.inputContainer}>
                             <span>{t("common.name")}</span>
                           </div>
                           <div className={styles.inputContainer}>
                             <span>{t("common.server")}</span>
                           </div>
                         </div>
                         {environments?.map(((environment) => (
                           <div className={styles.inputs}
                                key={environment.id}>
                             <div className={styles.inputContainer}>
                               <span>{environment.name}</span>
                             </div>
                             <div className={styles.inputContainer}>
                               <div className={styles.dropdown}>
                                 {getServer(localSelectedTestCaseTemplate, environment)?.name}
                               </div>
                             </div>
                           </div>
                         )))}
                       </div>
                     )
        }]}/>
      </div>
      <div className={styles.parameters}>
        <Accordion activeIndex={0}
                   activeClassName={styles.active}
                   maxHeight={400}
                   disabledAutoScroll
                   data={[{
          title: (
            <div className={styles.accordionTitle}>
              <img src={arrowIcon} alt=""
                   className={styles.arrow}/>
              <span>{t("testCaseTemplate.requestParameters")}</span>
            </div>
          ),
          child: (
            <div className={styles.accordionChild}>
              {localSelectedTestCaseTemplate.requestParameters.map((parameter, index) => (
                <div className={styles.parameter} key={index}>
                  <div>{t("common.name")}: <span className={styles.value}>{parameter.name}</span></div>
                  <div>{t("testCaseTemplate.value")}: <span className={styles.value}>{parameter.value}</span></div>
                </div>
              ))}
            </div>
          )
        }]}/>
      </div>
      <div className={styles.parameters}>
        <Accordion activeIndex={0}
                   maxHeight={400}
                   activeClassName={styles.active}
                   disabledAutoScroll
                   data={[{
          title: (
            <div className={styles.accordionTitle}>
              <img src={arrowIcon} alt=""
                   className={styles.arrow}/>
              <span>{t("testCaseTemplate.responseParameters")}</span>
            </div>
          ),
          child: (
            <div className={styles.accordionChild}>
              {localSelectedTestCaseTemplate.responseParameters.map((parameter, index) => (
                <div className={styles.parameter} key={index}>
                  <div>{t("common.name")}: <span className={styles.value}>{parameter.name}</span></div>
                  <div>{t("testCaseTemplate.value")}: <span className={styles.value}>{parameter.value}</span></div>
                </div>
              ))}
            </div>
          )
        }]}/>
      </div>
      <div className={`${styles.parameters} ${styles.headers}`}>
        <Accordion activeIndex={0}
                   activeClassName={styles.active}
                   maxHeight={400}
                   disabledAutoScroll
                   data={[{
          title: (
            <div className={styles.accordionTitle}>
            <img src={arrowIcon} alt=""
                   className={styles.arrow}/>
              <span>{t("testCaseTemplate.headers")}</span>
            </div>
          ),
          child: (
            <div className={styles.accordionChild}>
              {localSelectedTestCaseTemplate.requestHeaders.map((parameter, index) => (
                <div className={styles.parameter} key={index}>
                  <div>{t("common.name")}: <span className={styles.value}>{parameter.name}</span></div>
                  <div>{t("testCaseTemplate.value")}: <span className={styles.value}>{parameter.value}</span></div>
                </div>
              ))}
            </div>
          )
        }]}/>
      </div>
      <div className={styles.endpoint}>
        <div className={styles.inputContainer}>
          <span>{t("testCase.endpoint")}</span>
          <div className={styles.input}>
            <span title={localSelectedTestCaseTemplate.endpoint}>{localSelectedTestCaseTemplate.endpoint}</span>
          </div>
        </div>
      </div>
      <div className={styles.textAreaContainer}>
        <div className={styles.title}>
          {`${t("common.body")} / ${t("common.parametersString")}`}
        </div>
        <div className={styles.textarea}>
          {localSelectedTestCaseTemplate.payload || (<pre>{localSelectedTestCaseTemplate.body}</pre>)}
        </div>
      </div>
      <div className={styles.textAreaContainer}>
        <div className={styles.title}>
          {t("testCase.expectedResult")}
        </div>
        <div className={styles.textarea}>
          <pre>{localSelectedTestCaseTemplate.expectedBody}</pre>
        </div>
      </div>
    </div>
  );
}
